import { lazy, Suspense } from "react";
import { FileText } from "lucide-react";

// Lazy-load the PDF renderer so surfaces that only ever show server
// thumbnails / images don't pull it into their bundle.
const PdfThumbnail = lazy(() =>
  import("./citations/heavy-pdf").then((m) => ({ default: m.PdfThumbnail })),
);

export interface FileThumbnailProps {
  /** MIME type — picks the render strategy (pdf vs image vs generic). */
  contentType: string | null | undefined;
  /** URL to the original file bytes (used for the PDF / image render). */
  fileUrl: string;
  /**
   * Optional URL to a server-rendered thumbnail. When present it's used
   * directly (cheap `<img>`) in preference to a client-side render — the
   * fast path for files that already have a generated thumbnail.
   */
  thumbnailUrl?: string | undefined;
  /** PDF render resolution in px (retina: ~2× the card width). */
  size?: number;
  alt?: string;
}

/**
 * One file's preview, framed to fill a square slot (e.g. inside
 * `ArtifactThumbnailCard`). Pure presentational — takes resolved URLs, does
 * no fetching. Strategy, in order:
 *
 *   1. `thumbnailUrl` present → show it (`<img>`). The exact-match, cheap
 *      path for files with a server-rendered thumbnail.
 *   2. PDF → first page via the PDFium engine (lazy-loaded).
 *   3. Image → `<img>` of the original.
 *   4. Anything else → a generic file icon.
 *
 * The single thumbnail renderer across the app — uploads grid, lab
 * carousel, chat attachment strip, and the thread files panel.
 */
export function FileThumbnail({
  contentType,
  fileUrl,
  thumbnailUrl,
  size = 288,
  alt = "",
}: FileThumbnailProps) {
  if (thumbnailUrl) {
    return (
      <img src={thumbnailUrl} alt={alt} className="h-full w-full object-cover" loading="lazy" />
    );
  }

  const ct = (contentType ?? "").toLowerCase();

  if (ct.includes("pdf")) {
    return (
      <Suspense fallback={<FileText className="size-6 text-muted-foreground" />}>
        {/*
         * `PdfThumbnail` scales to cover the square — portrait pages fill
         * width and clip the bottom, landscape pages fill height and clip
         * the right. `items-start justify-center` anchors portrait pages to
         * the top so the header stays visible.
         */}
        <div className="flex h-full w-full items-start justify-center overflow-hidden">
          <PdfThumbnail fileUrl={fileUrl} size={size} />
        </div>
      </Suspense>
    );
  }

  if (ct.startsWith("image/")) {
    return <img src={fileUrl} alt={alt} className="h-full w-full object-cover" loading="lazy" />;
  }

  return <FileText className="size-6 text-muted-foreground" />;
}
